import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Tag, Image, ChevronLeft, ChevronRight, Plus, Minus, ShoppingCart, Check, Calendar, Trash2, ZoomIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../components/ui/confirm-dialog';
import { usePageTracking } from '../hooks/usePageTracking';
import { useSwipe } from '../hooks/useSwipe';
import { SEO } from '../components/SEO';
import { GoogleReviewsBadge } from '../components/GoogleReviewsBadge';
import { ReviewsReel } from '../components/ReviewsReel';
import { stripHtml } from '../utils/sanitizeHtml';
import { DescriptionContent } from '../components/DescriptionContent';
import VacationBanner from '../components/VacationBanner';
import { StoreDiscountBanner } from '../components/StoreDiscountBanner';
import ImageLightbox from '../components/ImageLightbox';
import { formatRelativeTime } from '../utils/dateFormat';
import axios from 'axios';
import { toast } from 'sonner';
import { optionLabel, buildCartName, singlePackLabel } from './shrimpRoom/utils';
import { StarRating } from '../components/StarRating';
import { useProductRating } from '../utils/reviewsCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper functions
const getAllImages = (shrimp) => {
  const images = [];
  if (shrimp.image_url) images.push(shrimp.image_url);
  if (shrimp.images && shrimp.images.length > 0) {
    shrimp.images.forEach(img => {
      if (img && !images.includes(img)) images.push(img);
    });
  }
  return images;
};

const getPrimaryImage = (shrimp) => {
  const images = getAllImages(shrimp);
  return images.length > 0 ? images[0] : null;
};

const getDisplayPrice = (shrimp, storeDiscount = 0) => {
  if (!shrimp.price) return null;
  // Individual discount takes priority, otherwise use store-wide discount
  const discountPercent = (shrimp.discount_percent && shrimp.discount_percent > 0) 
    ? shrimp.discount_percent 
    : storeDiscount;
  if (discountPercent > 0) {
    return shrimp.price * (1 - discountPercent / 100);
  }
  return shrimp.price;
};

const getEffectiveDiscount = (shrimp, storeDiscount = 0) => {
  // Individual discount takes priority
  if (shrimp.discount_percent && shrimp.discount_percent > 0) {
    return { percent: shrimp.discount_percent, name: shrimp.discount_name, isStoreWide: false };
  }
  if (storeDiscount > 0 && !shrimp.exclude_from_store_discount) {
    return { percent: storeDiscount, name: 'Store Sale', isStoreWide: true };
  }
  return null;
};

// Featured Shrimp Card Component (Compact version)
const FeaturedShrimpCard = ({ shrimp, onClick, storeDiscount = 0 }) => {
  const primaryImage = getPrimaryImage(shrimp);
  const rating = useProductRating(shrimp.id);
  const effectiveDiscount = getEffectiveDiscount(shrimp, storeDiscount);
  const discountPercent = effectiveDiscount?.percent || 0;
  
  // For cards, always show the lowest price (mid-grade if available, otherwise regular price)
  const lowestPrice = shrimp.pack_mode
    ? Math.min(...[shrimp.price, shrimp.pack_2_enabled ? shrimp.price_mid_grade : null].filter((v) => v != null))
    : (shrimp.grades_enabled && shrimp.price_mid_grade ? shrimp.price_mid_grade : shrimp.price);
  const displayPrice = discountPercent > 0 
    ? lowestPrice * (1 - discountPercent / 100)
    : lowestPrice;
  
  return (
    <Card 
      className="bg-surface border-shrimp-border/50 overflow-hidden group cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 w-52 flex-shrink-0 flex flex-col"
      onClick={onClick}
      data-testid={`featured-card-${shrimp.id}`}
    >
      {/* Image */}
      <div className="aspect-square bg-shrimp-beige relative overflow-hidden">
        {primaryImage ? (
          <img 
            src={primaryImage} 
            alt={shrimp.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Image className="w-10 h-10 text-shrimp-border" />
          </div>
        )}
        
        {/* Discount badge */}
        {!shrimp.coming_soon && discountPercent > 0 && (
          <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1">
            <Tag className="w-3 h-3" />
            -{discountPercent}%
          </div>
        )}

        {/* Coming Soon badge */}
        {shrimp.coming_soon && (
          <div className="absolute top-2 left-2 bg-amber-500 text-white px-2 py-0.5 rounded-full text-xs font-bold">
            Coming Soon
          </div>
        )}
        
        {/* Out of stock */}
        {!shrimp.in_stock && !shrimp.coming_soon && (
          <div className="absolute top-2 right-2 bg-gray-500 text-white px-2 py-0.5 rounded-full text-xs">
            Out
          </div>
        )}
      </div>

      <CardContent className="p-3 flex-1 flex flex-col">
        <h3 className="font-medium text-sm text-shrimp-dark mb-1 line-clamp-2 min-h-[2.5rem]">
          {shrimp.name}
        </h3>
        <div className="mb-1.5 min-h-[1.1rem] flex items-center gap-1.5 flex-wrap" data-testid={`featured-rating-${shrimp.id}`}>
          <StarRating value={rating?.average || 0} size="sm" />
          {rating && rating.count > 0 && (
            <span className="text-[10px] font-medium uppercase tracking-wide text-shrimp-text/55">Customer Reviews</span>
          )}
        </div>

        {/* Short description (truncated to 2 lines, plain text) */}
        {shrimp.description && (
          <p className="text-xs text-shrimp-text/80 leading-snug mb-1.5 line-clamp-2 min-h-[2rem]" data-testid="featured-card-description">
            {stripHtml(shrimp.description)}
          </p>
        )}
        
        {/* Grade indicator */}
        <div className="min-h-[1.25rem] mb-1">
          {shrimp.grades_enabled && (
            <p className="text-xs text-amber-600 font-medium">
              {shrimp.pack_mode ? `Available in ${optionLabel(shrimp, 'high')} & ${optionLabel(shrimp, 'mid')}` : 'Mid & High Grades Available'}
            </p>
          )}
        </div>
        
        {/* Price — pushed to bottom */}
        <div className="mt-auto">
          {shrimp.price ? (
            <div className="flex items-center gap-1.5 flex-wrap">
              {discountPercent > 0 ? (
                <>
                  <span className="text-xs text-red-400 line-through">
                    {shrimp.grades_enabled ? `€${shrimp.price_mid_grade?.toFixed(2)}+` : `€${shrimp.price.toFixed(2)}`}
                  </span>
                  <span className="text-sm font-bold text-shrimp-green">
                    {shrimp.grades_enabled ? 'from ' : ''}€{displayPrice.toFixed(2)}
                  </span>
                </>
              ) : (
                <span className="text-sm font-bold text-shrimp-dark">
                  {shrimp.grades_enabled 
                    ? `from €${lowestPrice.toFixed(2)}` 
                    : `€${shrimp.price.toFixed(2)}`}
                </span>
              )}
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

// Shrimp Detail Modal for Home Page
const ShrimpDetailModal = ({ shrimp, isOpen, onClose, storeDiscount = 0 }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedGrade, setSelectedGrade] = useState(null); // 'mid' or 'high'
  const { addItem } = useCart();
  
  const allImages = getAllImages(shrimp || {});
  
  // Get price based on selected grade
  const getGradePrice = () => {
    if (!shrimp) return null;
    if (!shrimp.grades_enabled) return shrimp.price;
    if (selectedGrade === 'mid') return shrimp.price_mid_grade;
    if (selectedGrade === 'high') return shrimp.price;
    return null;
  };
  
  const gradePrice = getGradePrice();
  const slotDiscount = (shrimp?.pack_mode && selectedGrade === 'mid') ? shrimp?.discount_percent_mid : shrimp?.discount_percent;
  const effectiveDiscount = gradePrice ? getEffectiveDiscount({ ...shrimp, price: gradePrice, discount_percent: slotDiscount }, storeDiscount) : getEffectiveDiscount(shrimp || {}, storeDiscount);
  const displayPrice = gradePrice ? getDisplayPrice({ ...shrimp, price: gradePrice, discount_percent: slotDiscount }, storeDiscount) : getDisplayPrice(shrimp || {}, storeDiscount);
  
  // Check if grades are sold out
  const midSoldOut = shrimp?.stock_quantity_mid_grade != null && shrimp.stock_quantity_mid_grade <= 0;
  const highSoldOut = shrimp?.stock_quantity_high_grade != null && shrimp.stock_quantity_high_grade <= 0;
  
  useEffect(() => {
    setCurrentImageIndex(0);
    setQuantity(1);
    setSelectedGrade(null);
  }, [shrimp, isOpen]);

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => prev === 0 ? allImages.length - 1 : prev - 1);
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => prev === allImages.length - 1 ? 0 : prev + 1);
  };

  const swipeHandlers = useSwipe({
    onSwipeLeft: () => allImages.length > 1 && setCurrentImageIndex(prev => prev === allImages.length - 1 ? 0 : prev + 1),
    onSwipeRight: () => allImages.length > 1 && setCurrentImageIndex(prev => prev === 0 ? allImages.length - 1 : prev - 1),
  });

  const handleAddToCart = () => {
    if (shrimp.coming_soon) {
      toast.info('This item is coming soon — check back later!');
      return;
    }
    if (!shrimp.in_stock) {
      toast.error('This item is currently out of stock');
      return;
    }
    
    // Check grade selection if grades are enabled
    if (shrimp.grades_enabled && !selectedGrade) {
      toast.error(shrimp.pack_mode ? 'Please select a pack size before adding to cart' : 'Please select a grade (Mid or High) before adding to cart');
      return;
    }
    
    // Create modified shrimp object with selected grade price
    const shrimpWithGrade = {
      ...shrimp,
      price: gradePrice,
      discount_percent: slotDiscount,
      exclude_from_store_discount: shrimp.pack_mode ? true : shrimp.exclude_from_store_discount,
      selected_grade: selectedGrade,
      name: buildCartName(shrimp, selectedGrade)
    };
    
    addItem(shrimpWithGrade, quantity, effectiveDiscount);
    toast.success(`${quantity} × ${shrimpWithGrade.name} added to basket!`);
    setQuantity(1);
  };

  if (!isOpen || !shrimp) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-surface border-shrimp-border max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-shrimp-dark flex items-center gap-3">
            {shrimp.color && (
              <span 
                className="px-3 py-1 rounded-full text-sm font-medium text-white"
                style={{ backgroundColor: shrimp.color }}
              >
                {shrimp.tag || shrimp.name.split(' ')[0]}
              </span>
            )}
            {shrimp.name}
          </DialogTitle>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6 mt-4">
          {/* Image Gallery */}
          <div className="space-y-3">
            <div className="aspect-square bg-shrimp-beige rounded-xl overflow-hidden relative" {...swipeHandlers}>
              {allImages.length > 0 ? (
                <>
                  <img loading="lazy" 
                    src={allImages[currentImageIndex]} 
                    alt={shrimp.name}
                    onClick={() => setLightboxOpen(true)}
                    className="w-full h-full object-cover cursor-zoom-in"
                    data-testid="home-modal-main-image"
                  />
                  {allImages.length > 1 && (
                    <>
                      <button 
                        onClick={handlePrevImage}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-surface/80 hover:bg-surface-hover p-2 rounded-full shadow-lg"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button 
                        onClick={handleNextImage}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-surface/80 hover:bg-surface-hover p-2 rounded-full shadow-lg"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </>
                  )}
                  {effectiveDiscount?.percent > 0 && (
                    <div className="absolute top-3 left-3 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                      -{effectiveDiscount.percent}% OFF
                    </div>
                  )}
                  <button type="button" onClick={() => setLightboxOpen(true)} className="absolute bottom-3 right-3 flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/55 text-white text-xs backdrop-blur hover:bg-black/70 transition-colors" data-testid="home-modal-zoom-button">
                    <ZoomIn className="w-3.5 h-3.5" /> Tap to zoom
                  </button>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Image className="w-16 h-16 text-shrimp-border" />
                </div>
              )}
            </div>
            <ImageLightbox images={allImages} open={lightboxOpen} index={currentImageIndex} onClose={() => setLightboxOpen(false)} />
            
            {/* Thumbnails */}
            {allImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {allImages.map((img, i) => (
                  <button
                    key={`${img}-${i}`}
                    onClick={() => setCurrentImageIndex(i)}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 flex-shrink-0 ${i === currentImageIndex ? 'border-shrimp-green' : 'border-transparent'}`}
                  >
                    <img loading="lazy" src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="space-y-4">
            {/* Grade Selection - if grades are enabled */}
            {shrimp.grades_enabled && shrimp.price && shrimp.price_mid_grade && (
              <div className="bg-gradient-to-r from-amber-50 to-emerald-50 rounded-xl p-4 border border-amber-200/50">
                <p className="text-sm font-medium text-shrimp-dark mb-3">Select Grade:</p>
                <div className="grid grid-cols-2 gap-3">
                  {/* Mid Grade Button */}
                  {(() => {
                    const midDiscount = getEffectiveDiscount({ ...shrimp, price: shrimp.price_mid_grade, discount_percent: shrimp.pack_mode ? shrimp.discount_percent_mid : shrimp.discount_percent }, storeDiscount);
                    const midDiscountedPrice = midDiscount?.percent > 0
                      ? (shrimp.price_mid_grade * (1 - midDiscount.percent / 100)).toFixed(2)
                      : null;
                    return (
                      <button
                        onClick={() => !midSoldOut && setSelectedGrade('mid')}
                        disabled={midSoldOut}
                        className={`relative p-4 rounded-xl border-2 transition-all ${shrimp.pack_mode ? 'order-2 ' : ''}${
                          midSoldOut 
                            ? 'bg-surface-alt border-divider cursor-not-allowed opacity-60' 
                            : selectedGrade === 'mid' 
                              ? 'bg-amber-100 border-amber-400 shadow-md' 
                              : 'bg-surface border-amber-200 hover:border-amber-300 hover:bg-amber-50'
                        }`}
                      >
                        <p className={`font-semibold ${midSoldOut ? 'text-subtle' : 'text-amber-700'}`}>{optionLabel(shrimp, 'mid')}</p>
                        {midSoldOut ? (
                          <p className="text-lg font-bold mt-1 text-subtle">Sold Out</p>
                        ) : midDiscountedPrice ? (
                          <div className="mt-1">
                            <p className="text-sm text-subtle line-through">€{shrimp.price_mid_grade?.toFixed(2)}</p>
                            <p className="text-lg font-bold text-green-600">€{midDiscountedPrice}</p>
                          </div>
                        ) : (
                          <p className="text-lg font-bold mt-1 text-amber-600">€{shrimp.price_mid_grade?.toFixed(2)}</p>
                        )}
                        <p className={`text-xs mt-1 ${midSoldOut ? 'text-subtle' : 'text-amber-600/70'}`}>
                          {midSoldOut ? 'Currently unavailable' : `${shrimp.stock_quantity_mid_grade || 0} available`}
                        </p>
                        {selectedGrade === 'mid' && !midSoldOut && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })()}
                  
                  {/* High Grade Button */}
                  {(() => {
                    const highDiscount = getEffectiveDiscount({ ...shrimp, price: shrimp.price }, storeDiscount);
                    const highDiscountedPrice = highDiscount?.percent > 0
                      ? (shrimp.price * (1 - highDiscount.percent / 100)).toFixed(2)
                      : null;
                    return (
                      <button
                        onClick={() => !highSoldOut && setSelectedGrade('high')}
                        disabled={highSoldOut}
                        className={`relative p-4 rounded-xl border-2 transition-all ${shrimp.pack_mode ? 'order-1 ' : ''}${
                          highSoldOut 
                            ? 'bg-surface-alt border-divider cursor-not-allowed opacity-60' 
                            : selectedGrade === 'high' 
                              ? 'bg-emerald-100 border-emerald-400 shadow-md' 
                              : 'bg-surface border-emerald-200 hover:border-emerald-300 hover:bg-emerald-50'
                        }`}
                      >
                        <p className={`font-semibold ${highSoldOut ? 'text-subtle' : 'text-emerald-700'}`}>{optionLabel(shrimp, 'high')}</p>
                        {highSoldOut ? (
                          <p className="text-lg font-bold mt-1 text-subtle">Sold Out</p>
                        ) : highDiscountedPrice ? (
                          <div className="mt-1">
                            <p className="text-sm text-subtle line-through">€{shrimp.price?.toFixed(2)}</p>
                            <p className="text-lg font-bold text-green-600">€{highDiscountedPrice}</p>
                          </div>
                        ) : (
                          <p className="text-lg font-bold mt-1 text-emerald-600">€{shrimp.price?.toFixed(2)}</p>
                        )}
                        <p className={`text-xs mt-1 ${highSoldOut ? 'text-subtle' : 'text-emerald-600/70'}`}>
                          {highSoldOut ? 'Currently unavailable' : `${shrimp.stock_quantity_high_grade || 0} available`}
                        </p>
                        {selectedGrade === 'high' && !highSoldOut && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })()}
                </div>
                {!selectedGrade && (
                  <p className="text-xs text-amber-600 mt-2 text-center">
                    Please select a grade to continue
                  </p>
                )}
              </div>
            )}
            
            {/* Price - show when no grades or when grade is selected */}
            {((shrimp.price && !shrimp.grades_enabled) || (shrimp.grades_enabled && selectedGrade && gradePrice)) && (
              <div className="bg-shrimp-bg rounded-xl p-4">
                <p className="text-sm text-shrimp-text mb-1">
                  Price per item
                  {shrimp.grades_enabled && selectedGrade && (
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${selectedGrade === 'high' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {optionLabel(shrimp, selectedGrade)}
                    </span>
                  )}
                </p>
                {effectiveDiscount?.percent > 0 ? (
                  <div className="flex items-center gap-3">
                    <span className="text-lg text-red-400 line-through">€{(gradePrice || shrimp.price).toFixed(2)}</span>
                    <span className="text-3xl font-bold text-shrimp-green">€{displayPrice.toFixed(2)}</span>
                  </div>
                ) : (
                  <span className="text-3xl font-bold text-shrimp-dark">€{(gradePrice || shrimp.price).toFixed(2)}</span>
                )}
              </div>
            )}

            {/* Description */}
            {shrimp.description && (
              <div>
                <h4 className="font-medium text-shrimp-dark mb-2">About this colony</h4>
                <DescriptionContent text={shrimp.description} className="text-shrimp-text text-sm leading-relaxed" />
                {shrimp.slug && (
                  <Link
                    to={`/product/${shrimp.slug}`}
                    onClick={onClose}
                    className="inline-block mt-3 text-blue-600 hover:text-blue-800 font-semibold text-base underline underline-offset-4"
                    data-testid="more-information-link"
                  >
                    More Information
                  </Link>
                )}
              </div>
            )}

            {/* Add to Cart */}
            {shrimp.coming_soon ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center" data-testid="coming-soon-modal-notice">
                <p className="text-amber-700 font-semibold text-lg">🦐 Coming Soon</p>
                <p className="text-sm text-amber-600 mt-1">This item is not yet available for purchase. Check back soon!</p>
              </div>
            ) : shrimp.in_stock ? (
              <div className="bg-shrimp-beige rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="flex items-center border border-shrimp-border rounded-lg bg-surface">
                    <button 
                      onClick={() => setQuantity(q => Math.max(1, q - 1))}
                      className="p-2 hover:bg-shrimp-bg rounded-l-lg"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <Input
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-16 text-center border-0 focus:ring-0"
                      min="1"
                    />
                    <button 
                      onClick={() => setQuantity(q => q + 1)}
                      className="p-2 hover:bg-shrimp-bg rounded-r-lg"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <Button 
                    onClick={handleAddToCart}
                    disabled={shrimp.grades_enabled && !selectedGrade}
                    className={`flex-1 ${shrimp.grades_enabled && !selectedGrade ? 'bg-gray-300 cursor-not-allowed' : 'bg-shrimp-green hover:bg-shrimp-text'} text-white`}
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    {shrimp.grades_enabled && !selectedGrade ? 'Select Grade' : 'Add to Basket'}
                  </Button>
                </div>
                {((shrimp.price && !shrimp.grades_enabled) || (shrimp.grades_enabled && selectedGrade && gradePrice)) && (
                  <p className="text-center text-sm text-shrimp-text mt-2">
                    Total: <strong>€{(displayPrice * quantity).toFixed(2)}</strong>
                  </p>
                )}
              </div>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                <p className="text-red-600 font-medium">Currently Out of Stock</p>
                <p className="text-sm text-red-500 mt-1">Check back soon!</p>
              </div>
            )}

            {/* Link to Shrimp Room */}
            <Link to="/shrimp-room" className="block">
              <Button variant="outline" className="w-full border-shrimp-green text-shrimp-green hover:bg-shrimp-green hover:text-white">
                View All Shrimp in Shrimp Room
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Updates Slideshow — full-width cards, one at a time with arrows
const UpdatesSlideshow = ({ updates, isAdmin, onDelete }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goTo = (idx) => {
    if (idx < 0) setCurrentIndex(updates.length - 1);
    else if (idx >= updates.length) setCurrentIndex(0);
    else setCurrentIndex(idx);
  };

  const update = updates[currentIndex];
  if (!update) return null;

  return (
    <div data-testid="updates-slideshow">
      <Card className="bg-surface border-shrimp-border/50 overflow-hidden group" data-testid={`update-card-${update.id}`}>
        {update.image_url && (
          <div className="bg-shrimp-beige overflow-hidden">
            <img 
              src={update.image_url} 
              alt="Update"
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </div>
        )}
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-shrimp-text flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatRelativeTime(update.created_at)}
            </span>
            {isAdmin && (
              <button 
                onClick={() => onDelete(update.id)}
                className="p-1.5 hover:bg-red-50 rounded text-red-400 hover:text-red-500 transition-colors"
                data-testid={`delete-update-${update.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
          <p className="text-shrimp-dark font-body text-sm leading-relaxed whitespace-pre-wrap">
            {update.content}
          </p>
        </CardContent>
      </Card>

      {/* Navigation */}
      {updates.length > 1 && (
        <div className="flex items-center justify-center gap-4 mt-4">
          <button onClick={() => goTo(currentIndex - 1)} className="p-2 rounded-full bg-surface border border-shrimp-border hover:bg-shrimp-beige transition-colors" data-testid="updates-prev-btn">
            <ChevronLeft className="w-5 h-5 text-shrimp-dark" />
          </button>
          <div className="flex gap-1.5">
            {updates.map((update, idx) => (
              <button
                key={update.id || update._id || `update-dot-${idx}`}
                onClick={() => goTo(idx)}
                className={`w-2 h-2 rounded-full transition-colors ${idx === currentIndex ? 'bg-shrimp-green' : 'bg-shrimp-border'}`}
                data-testid={`updates-dot-${idx}`}
              />
            ))}
          </div>
          <button onClick={() => goTo(currentIndex + 1)} className="p-2 rounded-full bg-surface border border-shrimp-border hover:bg-shrimp-beige transition-colors" data-testid="updates-next-btn">
            <ChevronRight className="w-5 h-5 text-shrimp-dark" />
          </button>
        </div>
      )}
    </div>
  );
};

// Add Update Dialog for admin
const AddUpdateDialog = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setSubmitting(true);
    try {
      await onAdd({ content: content.trim(), image_url: imageUrl || null });
      setContent('');
      setImageUrl('');
      setOpen(false);
      toast.success('Update posted!');
    } catch (error) {
      toast.error('Failed to post update');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-shrimp-green hover:bg-shrimp-text text-white rounded-full" data-testid="add-update-btn">
          <Plus className="w-4 h-4 mr-1" /> Post Update
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-surface border-shrimp-border">
        <DialogHeader>
          <DialogTitle className="font-display text-lg text-shrimp-dark">Share an Update</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <Textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="What's happening in the shrimp room?" className="bg-shrimp-bg border-shrimp-border resize-none" rows={3} maxLength={280} data-testid="update-content" />
          <p className="text-xs text-shrimp-text text-right">{content.length}/280</p>
          <div>
            <label className="text-sm text-shrimp-dark flex items-center gap-2 mb-1"><Image className="w-4 h-4" /> Image URL (optional)</label>
            <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://..." className="bg-shrimp-bg border-shrimp-border" data-testid="update-image" />
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1 border-shrimp-border">Cancel</Button>
            <Button type="submit" disabled={!content.trim() || submitting} className="flex-1 bg-shrimp-green hover:bg-shrimp-text" data-testid="submit-update">{submitting ? 'Posting...' : 'Post'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const Home = () => {
  const [settings, setSettings] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [highlightedShrimp, setHighlightedShrimp] = useState([]);
  const [comingSoonShrimp, setComingSoonShrimp] = useState([]);
  const [selectedShrimp, setSelectedShrimp] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [welcomeExpanded, setWelcomeExpanded] = useState(false);
  const { user } = useAuth();
  const isAdmin = !!user;
  const confirm = useConfirm();
  
  // Track page visit
  usePageTracking('home');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, postsRes, shrimpRes, updatesRes] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/blog`),
        axios.get(`${API}/shrimp`),
        axios.get(`${API}/updates`)
      ]);
      setSettings(settingsRes.data);
      setRecentPosts(postsRes.data.slice(0, 3));
      setUpdates(updatesRes.data.slice(0, 10));
      
      const allShrimp = shrimpRes.data.filter(s => s.in_stock);
      
      // Get highlighted shrimp: prioritize discounted ones, fill with random
      const discounted = allShrimp
        .filter(s => !s.coming_soon && s.discount_percent && s.discount_percent > 0)
        .sort((a, b) => b.discount_percent - a.discount_percent);
      
      let highlights = [...discounted.slice(0, 5)];
      
      // Fill remaining slots with random non-discounted shrimp (exclude coming_soon — they have their own row)
      if (highlights.length < 5) {
        const nonDiscounted = allShrimp.filter(s => !s.coming_soon && (!s.discount_percent || s.discount_percent === 0));
        const shuffled = nonDiscounted.sort(() => Math.random() - 0.5);
        highlights = [...highlights, ...shuffled.slice(0, 5 - highlights.length)];
      }
      
      setHighlightedShrimp(highlights.slice(0, 5));

      // Coming-soon row — separate list (up to 5)
      const comingSoonList = shrimpRes.data
        .filter(s => s.coming_soon)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setComingSoonShrimp(comingSoonList.slice(0, 5));
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // Update handlers
  const handleAddUpdate = async (data) => {
    const response = await axios.post(`${API}/updates`, data);
    setUpdates([response.data, ...updates].slice(0, 10));
  };

  const handleDeleteUpdate = async (id) => {
    if (!(await confirm({ title: 'Delete update?', description: 'Delete this update?', confirmText: 'Delete', destructive: true }))) return;
    try {
      await axios.delete(`${API}/updates/${id}`);
      setUpdates(updates.filter(u => u.id !== id));
      toast.success('Update deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Calculate active store discount from scheduled discounts
  const [scheduledDiscount, setScheduledDiscount] = useState(null);
  
  useEffect(() => {
    const fetchScheduledDiscount = async () => {
      try {
        const response = await axios.get(`${API}/scheduled-discounts/active`);
        if (response.data.has_active_discount) {
          setScheduledDiscount(response.data.discount);
        }
      } catch (error) {
        // No active scheduled discount
      }
    };
    fetchScheduledDiscount();
  }, []);

  const getActiveStoreDiscount = () => {
    // First check scheduled discounts (new system)
    if (scheduledDiscount?.is_enabled && scheduledDiscount?.discount_percent) {
      return scheduledDiscount.discount_percent;
    }
    
    // Fall back to settings (old system)
    if (!settings?.store_discount_enabled || !settings?.store_discount_percent) return 0;
    const now = new Date();
    if (settings.store_discount_start_date) {
      const startDate = new Date(settings.store_discount_start_date);
      if (now < startDate) return 0;
    }
    if (settings.store_discount_end_date) {
      const endDate = new Date(settings.store_discount_end_date);
      endDate.setHours(23, 59, 59, 999);
      if (now > endDate) return 0;
    }
    return settings.store_discount_percent;
  };
  
  const activeStoreDiscount = getActiveStoreDiscount();

  return (
    <div className="animate-fade-in" data-testid="home-page">
      <SEO 
        pageKey="home"
        title="Buy Aquarium Shrimp Ireland | Freshwater Neocaridina Shrimp"
        description="Buy freshwater aquarium shrimp in Ireland. DAFM-registered breeder offering Neocaridina shrimp — Bloody Mary, Blue Dream, Green Jade, Cherry Shrimp & more. Free collection in Cork or shipped nationwide via UPS."
        url="https://www.justkeepshrimping.com/"
        faq={[
          {
            question: "Where can I buy aquarium shrimp in Ireland?",
            answer: "Just Keep Shrimping is a DAFM-registered home breeder based in Cork, Ireland. We sell freshwater aquarium shrimp including Bloody Mary, Blue Dream, Green Jade, Cherry Shrimp and more. Order online for nationwide UPS delivery or collect in person from Cork."
          },
          {
            question: "Do you ship shrimp across Ireland?",
            answer: "Yes, we ship nationwide across Ireland using UPS Live Animal Service. Shrimp are dispatched Monday to Wednesday with professional-grade insulated packaging and heat packs. A tracking number is provided with every order."
          },
          {
            question: "What types of freshwater shrimp do you sell?",
            answer: "We breed and sell Neocaridina davidi shrimp in several colour varieties including Bloody Mary (Orange Eyes), Deep Blue (Orange Eyes), Dark Green Jade, Yellow Neon, Orange Sakura, Choko Sakura, and Blue Dream. All are selectively bred for genetic stability and vibrant colour."
          },
          {
            question: "Are your shrimp DAFM registered?",
            answer: "Yes, Just Keep Shrimping is registered with the Department of Agriculture, Food and the Marine (DAFM) as a Seller and Supplier of Pet Animals. Registration No: DPT200351C. All shrimp are home-bred in Ireland."
          },
          {
            question: "How much do pet shrimp cost in Ireland?",
            answer: "Our freshwater aquarium shrimp start from as low as \u20ac1.85 each. Prices vary by colour line and grade. We also offer bulk discounts \u2014 buy 10 get 1 free, buy 20 get 3 free (on individual shrimp). Check our Shrimp Room for current pricing and availability."
          }
        ]}
      />
      {/* Hero Section - Logo + Welcome */}
      <section className="hero-gradient py-6 md:py-10" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-3 flex flex-col items-center justify-center">
              <img 
                src={`${API}/logo/with_text`} 
                alt="Just Keep Shrimping" 
                className="w-64 sm:w-80 lg:w-96 h-auto object-contain"
                width="384"
                height="384"
                loading="eager"
                fetchPriority="high"
              />
              {settings?.home_hero_subtitle && (
                <p className="font-display text-lg sm:text-xl text-shrimp-dark mt-2 italic" data-testid="hero-subtitle">
                  {settings.home_hero_subtitle}
                </p>
              )}
              <p className="text-sm text-shrimp-text mt-2">
                Owned and Operated by: Sanjin Blazinic
              </p>
            </div>

            {/* Welcome Block */}
            {(settings?.home_welcome_title || settings?.home_welcome_text) && (
              <div className="mt-6 md:mt-8" data-testid="hero-welcome-block">
                {settings?.home_welcome_title && (
                  <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-shrimp-dark mb-4" data-testid="hero-welcome-title">
                    {settings.home_welcome_title}
                  </h2>
                )}
                {settings?.home_welcome_text && (
                  <div className="max-w-3xl mx-auto mb-6">
                    <p className={`font-body text-base lg:text-lg text-shrimp-text leading-relaxed whitespace-pre-line ${welcomeExpanded ? '' : 'line-clamp-6 sm:line-clamp-none'}`} data-testid="hero-welcome-text">
                      {settings.home_welcome_text}
                    </p>
                    <button
                      type="button"
                      onClick={() => setWelcomeExpanded((v) => !v)}
                      className="sm:hidden mt-2 text-sm font-medium text-shrimp-green hover:text-shrimp-text"
                      data-testid="hero-welcome-toggle"
                    >
                      {welcomeExpanded ? 'Show less' : 'Read more'}
                    </button>
                  </div>
                )}
                <div className="flex justify-center">
                  <Link to="/shrimp-room">
                    <Button 
                      className="bg-shrimp-green hover:bg-shrimp-text text-white rounded-full px-8 py-3 font-medium"
                      data-testid="hero-cta-button"
                    >
                      {settings?.home_cta_text || 'Explore Our Shrimp'}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Featured Shrimp Section - Olive green background with CTA */}
      <section className="py-6 md:py-8 bg-shrimp-green" data-testid="highlights-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-4">
            <h2 className="font-display text-3xl font-bold text-white mb-2" data-testid="home-shrimp-section-title">
              {settings?.home_shrimp_section_title || 'Browse Our Aquarium Shrimp'}
            </h2>
            <p className="text-white/90 font-body max-w-2xl mx-auto" data-testid="home-shrimp-section-subtitle">
              {settings?.home_shrimp_section_subtitle || "Explore our collection of freshwater Neocaridina shrimp. From vibrant Cherry Shrimp to stunning Blue Dreams — see what's available in our tanks."}
            </p>
            {settings?.google_reviews_show_home && settings?.google_reviews_url && (
              <div className="mt-4 flex justify-center" data-testid="home-google-reviews">
                <GoogleReviewsBadge settings={settings} />
              </div>
            )}
            {settings?.reviews_reel_show_home !== false && <ReviewsReel onDark className="mt-6" />}
          </div>

          {/* Weather Suspension Banner */}
          {settings?.weather_suspension_active && (
            <div className="bg-amber-100 border-2 border-amber-400 rounded-lg p-4 mb-6 max-w-2xl mx-auto" data-testid="weather-banner-home">
              <div className="flex items-start gap-3">
                <span className="text-2xl">⛈️</span>
                <div>
                  <h4 className="font-semibold text-amber-800">Weather Suspension Active</h4>
                  <p className="text-amber-700 text-sm mt-1">{settings.weather_suspension_message}</p>
                </div>
              </div>
            </div>
          )}

          {/* Vacation Mode Banner (scheduled: advance notice -> on-holiday -> auto-off) */}
          <VacationBanner settings={settings} className="max-w-2xl mx-auto" />

          {/* Store-wide Discount Banner */}
          <StoreDiscountBanner settings={settings} className="max-w-2xl mx-auto" testId="store-discount-banner-home" />
          
          {/* Featured Shrimp Cards */}
          {highlightedShrimp.length > 0 && (
            <div className="flex gap-4 overflow-x-auto pb-4 mb-8 px-4 sm:px-0 sm:justify-center scroll-smooth snap-x snap-mandatory items-stretch">
              {highlightedShrimp.map((shrimp) => (
                <div key={shrimp.id} className="snap-start flex-shrink-0 flex">
                  <FeaturedShrimpCard 
                    shrimp={shrimp} 
                    onClick={() => setSelectedShrimp(shrimp)}
                    storeDiscount={activeStoreDiscount}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Coming Soon row */}
          {comingSoonShrimp.length > 0 && (
            <div className="mt-12 mb-8" data-testid="coming-soon-home-row">
              <div className="text-center mb-4">
                <h2 className="font-display text-3xl font-bold text-white mb-2" data-testid="coming-soon-heading">
                  {settings?.coming_soon_heading || 'Coming Soon'}
                </h2>
                <p className="text-white/90 font-body max-w-2xl mx-auto" data-testid="coming-soon-subtitle">
                  {settings?.coming_soon_subtitle || "We're carefully cultivating the next generation of colonies — these little stunners will be ready to ship very soon. Keep an eye out."}
                </p>
              </div>
              <div className="flex gap-4 overflow-x-auto pb-4 px-4 sm:px-0 sm:justify-center scroll-smooth snap-x snap-mandatory items-stretch">
                {comingSoonShrimp.map((shrimp) => (
                  <div key={shrimp.id} className="snap-start flex-shrink-0 flex">
                    <FeaturedShrimpCard
                      shrimp={shrimp}
                      onClick={() => setSelectedShrimp(shrimp)}
                      storeDiscount={activeStoreDiscount}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* CTA Button */}
          <div className="text-center">
            <Link to="/shrimp-room">
              <Button className="bg-surface text-shrimp-green hover:bg-shrimp-beige rounded-full px-8 py-3 font-medium">
                Enter Shrimp Room
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Shrimp Detail Modal */}
      <ShrimpDetailModal 
        shrimp={selectedShrimp}
        isOpen={!!selectedShrimp}
        onClose={() => setSelectedShrimp(null)}
        storeDiscount={activeStoreDiscount}
      />

      {/* SEO Content Section — dirty white background + CTA buttons */}
      <section className="py-6 md:py-8 bg-shrimp-bg" data-testid="seo-intro-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-display text-2xl md:text-3xl font-bold text-shrimp-dark mb-3">
            {settings?.seo_section_title || (!settings ? '\u00A0' : "Freshwater Aquarium Shrimp for Sale in Ireland")}
          </h1>
          <p className="font-body text-base text-shrimp-text leading-relaxed mb-2">
            {settings?.seo_section_text || (!settings ? '\u00A0' : "Looking to buy aquarium shrimp in Ireland? Just Keep Shrimping is a DAFM-registered home breeder based in Cork, offering a wide selection of colourful freshwater pet shrimp. Whether you're setting up your first shrimp tank or adding to an established aquarium, we have hardy, home-bred Neocaridina shrimp ready to ship nationwide via UPS — or collect for free in Cork.")}
          </p>
          <p className="font-body text-sm text-shrimp-muted leading-relaxed mb-5">
            {settings?.seo_section_subtext || (!settings ? '\u00A0' : "Our ornamental shrimp are selectively bred by geneticists for vibrant colour and genetic stability. Browse Cherry Shrimp, Bloody Mary, Blue Dream, Green Jade, Fire Red and more — from just €1.85 each.")}
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/shrimp-room">
              <Button 
                className="bg-shrimp-green hover:bg-shrimp-text text-white rounded-full px-8 py-3 font-medium"
                data-testid="explore-shrimp-button"
              >
                {settings?.home_cta_text || (!settings ? "Shop Now" : "Explore Our Shrimp")}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/blog">
              <Button 
                variant="outline"
                className="border-2 border-shrimp-green text-shrimp-green hover:bg-shrimp-green hover:text-white rounded-full px-8 py-3"
                data-testid="read-blog-button"
              >
                Read Our Blog
              </Button>
            </Link>
            <Link to="/equipment">
              <Button 
                variant="outline"
                className="border-2 border-shrimp-green text-shrimp-green hover:bg-shrimp-green hover:text-white rounded-full px-8 py-3"
                data-testid="equipment-suggestions-button"
              >
                Our Equipment Suggestions
              </Button>
            </Link>
            <Link to="/gallery">
              <Button 
                variant="outline"
                className="border-2 border-shrimp-green text-shrimp-green hover:bg-shrimp-green hover:text-white rounded-full px-8 py-3"
                data-testid="gallery-button"
              >
                Check our Gallery
              </Button>
            </Link>
            <Link to="/about">
              <Button 
                variant="outline"
                className="border-2 border-shrimp-green text-shrimp-green hover:bg-shrimp-green hover:text-white rounded-full px-8 py-3"
                data-testid="about-us-button"
              >
                Who we are
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Genetics Banner */}
      <section className="bg-shrimp-bg py-4" data-testid="genetics-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-shrimp-dark font-medium text-sm">
            🧬 <span className="font-semibold">Science Meets Hobby</span> — Run by two geneticists passionate about selective breeding and the beautiful genetics of Neocaridina shrimp!
          </p>
        </div>
      </section>

      {/* Recent Blog Posts — warm beige background */}
      {recentPosts.length > 0 && (
        <section className="py-8 bg-shrimp-peach" data-testid="recent-posts-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6">
              <h2 className="font-display text-3xl font-bold text-shrimp-dark mb-2">
                Latest from the Blog
              </h2>
              <p className="text-shrimp-text font-body">
                Stories, tips, and updates from our shrimp keeping journey
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {recentPosts.map((post, index) => (
                <Link key={post.id} to={`/blog/${post.id}`}>
                  <Card 
                    className="h-full bg-shrimp-bg border-shrimp-border/50 card-lift overflow-hidden"
                    data-testid={`recent-post-${index}`}
                  >
                    {post.image_url && (
                      <div className="aspect-video bg-shrimp-beige overflow-hidden">
                        <img 
                          src={post.image_url} 
                          alt={post.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    )}
                    <CardContent className="p-6">
                      <h3 className="font-display text-xl font-semibold text-shrimp-dark mb-2 line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-shrimp-text text-sm line-clamp-3">
                        {post.excerpt}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/blog">
                <Button 
                  variant="outline"
                  className="border-shrimp-green text-shrimp-green hover:bg-shrimp-green hover:text-white rounded-full"
                  data-testid="view-all-posts-button"
                >
                  View All Posts
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Updates Section — Slideshow */}
      {updates.length > 0 && (
        <section className="py-10 md:py-14" style={{backgroundColor: 'rgb(252, 250, 246)'}} data-testid="updates-section">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6">
              <h2 className="font-display text-3xl font-bold text-shrimp-dark mb-2">
                Latest Updates
              </h2>
              <p className="text-shrimp-text font-body">
                Quick updates from the shrimp room
              </p>
            </div>

            {isAdmin && (
              <div className="flex justify-center mb-4">
                <AddUpdateDialog onAdd={handleAddUpdate} />
              </div>
            )}

            {/* Slideshow — one card at a time, full width */}
            <UpdatesSlideshow updates={updates} isAdmin={isAdmin} onDelete={handleDeleteUpdate} />

            {/* View All Link */}
            <div className="text-center mt-6">
              <Link to="/updates">
                <Button 
                  variant="outline" 
                  className="border-shrimp-green text-shrimp-green hover:bg-shrimp-green hover:text-white rounded-full"
                  data-testid="view-all-updates-btn"
                >
                  View All Updates
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
